import { Input } from '@frontend/shadcn/components/ui/input';
import { PlusIcon, MinusIcon, ClipboardIcon, GripDotsVerticalIcon, XmarkIcon, CheckIcon, PenIcon, TrashIcon } from '@revfluence/fresh-icons/solid/esm';
import React, { useEffect, useMemo, useState } from 'react';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@frontend/shadcn/components/ui/accordion';
import { RichTextEditor } from '@frontend/app/refresh-components';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Modal, Switch } from '@revfluence/fresh';
import { Separator } from '@frontend/shadcn/components/ui/separator';
import { useProjectByIdQuery } from '@frontend/app/hooks';
import { useParams } from 'react-router-dom';
import { getLabelForFieldType } from '@frontend/app/containers/Communities/AddOrEditCommunity/CommunityIdentity/utils';
import { isEmpty } from 'lodash';
import { SelectCombobox } from '@frontend/app/refresh-components/SelectCombobox';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { useRecruitment, useAmplifyHomePage } from '../RecruitmentContext';
import { useDefaultProjectInput } from '../../../LandingPages/hooks';
import { useFields, useSchemas } from '../../../LandingPages/LeftPanel/hooks';
import { IField } from '../../../LandingPages/types';
import { NewFormFieldModal } from './NewFormFieldModal';

export const AmplifyFormSection = () => {
  const {
    setAmplifyFormTitle,
    setAmplifyFormDescription,
    setAmplifyFormHasDescription,
    setAmplifyFormShowSection,
    setFormFields,
    addFormField,
    removeFormField,
    editFormField,
    reorderFormFields,
    setShowNewFormFieldModal,
  } = useRecruitment();

  const homePage = useAmplifyHomePage();

  const [isAddingField, setIsAddingField] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState<number | null>(null);

  const [newField, setNewField] = useState<IField>({
    id: null,
    name: null,
    dbName: null,
    type: null,
    label: null,
    required: false,
    choices: null,
    order: null,
    source: null,
  });

  const {
    projectId,
  } = useParams<{
    projectId: string;
  }>();

  const {
    data: {
      project = undefined,
    } = {},
  } = useProjectByIdQuery({
    variables: {
      id: Number(projectId) || undefined,
    },
    skip: !projectId,
  });

  const {
    applicationFormFields,
  } = useDefaultProjectInput(project);

  const defaultFields = useFields(applicationFormFields);

  useEffect(() => {
    if (applicationFormFields && !homePage.form.fields.length) {
      setFormFields(defaultFields);
    }
  }, [applicationFormFields, setFormFields, defaultFields, homePage.form.fields.length]);

  const {
    nonAppSchemas,
    appSchemas,
    refetchSchemas
  } = useSchemas();

  // we allow user to modify non-app-schemas and app-schemas only if schema.metaData.edtiable
  const allSchemas = useMemo(() => [
    ...nonAppSchemas,
    ...appSchemas.filter((schema) => schema.metaData?.editable),
  ], [nonAppSchemas, appSchemas]);

  if (!homePage) {
    return null;
  }

  const {
    form,
  } = homePage;

  const {
    title: formTitle,
    description: formDescription,
    hasDescription: formHasDescription,
    showSection: formShowSection,
    fields,
  } = form;

  const handleChangeFieldSchema = (schemaId: number) => {
    const schema = allSchemas.find((schema) => schema.id === schemaId);
    if (schema) {
      setNewField({
        ...newField,
        id: schema.id,
        name: schema.name,
        type: schema.type,
        label: schema.name,
        choices: schema.choices,
        source: 'schema',
      });
    }
  };

  const handleAddField = () => {
    addFormField({
      id: newField.id,
      name: newField.name,
      dbName: newField.dbName,
      type: newField.type,
      label: newField.label,
      required: newField.required,
      choices: newField.choices,
      order: newField.order,
      source: newField.source,
      active: true,
    });
    setIsAddingField(false);
    setNewField({
      id: null,
      name: null,
      dbName: null,
      type: null,
      label: null,
      required: false,
      choices: null,
      order: null,
      source: null,
    });
  };

  const handleEditField = (fieldId: number) => {
    setEditingFieldId(fieldId);
    const field = fields.find((field) => field.id === fieldId);
    if (field) {
      setNewField({
        id: field.id,
        name: field.name,
        dbName: field.dbName,
        type: field.type,
        label: field.label,
        required: field.required,
        choices: field.choices,
        order: field.order,
        source: field.source,
        active: true,
      });
    }
  };

  const handleCancelEditField = () => {
    setEditingFieldId(null);
    setNewField({
      id: null,
      name: null,
      dbName: null,
      type: null,
      label: null,
      required: false,
      choices: null,
      order: null,
      source: null,
    });
  };

  const handleSaveEditedField = (fieldId: number) => {
    editFormField(fieldId, newField);
    setEditingFieldId(null);
    setNewField({
      id: null,
      name: null,
      dbName: null,
      type: null,
      label: null,
      required: false,
      choices: null,
      order: null,
      source: null,
    });
  };

  const handleRemoveField = (fieldId: number) => {
    Modal.confirm({
      title: 'Remove Field',
      content: 'Are you sure you want to remove this field?',
      onOk: () => {
        removeFormField(fieldId);
        setEditingFieldId(null);
      },
    });
  };

  const handleCancelAddField = () => {
    setIsAddingField(false);
    setNewField({
      id: null,
      name: null,
      dbName: null,
      type: null,
      label: null,
      required: false,
      choices: null,
      order: null,
      source: null,
    });
  };

  const handleOnDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(fields);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    reorderFormFields(items.map((item, index) => ({ ...item, order: index })));
  };

  const handleSaveNewField = async (field: IField) => {
    await refetchSchemas();
    handleChangeFieldSchema(field.id);
  };

  return (
    <AccordionItem value="form" className="border-b-0">
      <AccordionTrigger>
        <div className="flex items-center gap-2 w-full">
          <div className="w-6 h-6 bg-purple-5 rounded-lg text-primary-foreground flex items-center justify-center">
            <ClipboardIcon fontSize={12} />
          </div>
          <span className="text-base font-medium">Form</span>
          <div className="ml-auto mr-2 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <Switch
              checked={formShowSection}
              onChange={(checked) => setAmplifyFormShowSection(checked)}
              size="small"
            />
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent>
        {/* Title Section */}
        <div className="flex flex-col gap-2">
          <label htmlFor="amplifyFormTitle" className="text-xs font-medium text-foreground">Title <span className="text-primaryMuted font-normal">(Required)</span></label>
          <div className="px-[1px]">
            <Input id="amplifyFormTitle" placeholder="Join Our Affiliate Program!" value={formTitle} onChange={(e) => setAmplifyFormTitle(e.target.value)} />
          </div>
        </div>

        {/* Description Section */}
        <div className="mt-2">
          {formHasDescription && (
          <div className="flex flex-col items-start gap-2">
            <Button
              variant="link"
              className="gap-2 pl-0"
              onClick={() => setAmplifyFormHasDescription(false)}
              size="xs"
            >
              <MinusIcon />
              Remove Description
            </Button>
            <RichTextEditor
              placeholder="Welcome to your page! Write something amazing about your brand and give creators more information about this collaboration."
              value={formDescription}
              onChange={setAmplifyFormDescription}
              className="[&_.ql-editor]:min-h-[100px]"
            />
          </div>
            )}
          {!formHasDescription && (
          <Button
            variant="link"
            className="gap-2 pl-0"
            onClick={() => setAmplifyFormHasDescription(true)}
            size="xs"
          >
            <PlusIcon />
            Add Description
          </Button>
            )}
        </div>

        <Separator className="my-4" />

        <div className="flex flex-col gap-2">
          <div className="flex flex-col justify-start items-start gap-2">
            <div className="self-stretch flex justify-between items-center gap-4">
              <div className="flex-1 text-foreground text-xs font-medium">Selected Fields</div>
              <Button variant="link" size="xs" disabled={isAddingField || !!editingFieldId} onClick={() => setIsAddingField(true)}>
                <PlusIcon className="mr-2" />
                Add Field
              </Button>
            </div>

            <DragDropContext onDragEnd={handleOnDragEnd}>
              <Droppable droppableId="memberFieldSchemas">
                {(provided) => (
                  <div {...provided.droppableProps} ref={provided.innerRef} className="self-stretch flex flex-col gap-2">
                    {fields?.map((field, index) => (
                      <Draggable key={field.id.toString()} draggableId={field.id.toString()} index={index}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className="self-stretch"
                          >
                            {editingFieldId === field.id ? (
                              <div className="px-3 py-2 bg-background rounded-xl border border-border flex flex-col gap-2">
                                <div className="flex flex-col gap-2">
                                  <div className="flex items-center gap-2">
                                    <div {...provided.dragHandleProps} className="flex items-center justify-center">
                                      <GripDotsVerticalIcon fontSize={12} className="text-grey-3" />
                                    </div>
                                    <label className="text-xs font-medium">Edit Field</label>
                                    <div className="flex items-center gap-2 ml-auto">
                                      <Switch size="small" checked={newField.required} onChange={(checked) => setNewField({ ...newField, required: checked })} />
                                      <span className="text-foreground text-sm">Required</span>
                                    </div>
                                  </div>
                                  <SelectCombobox
                                    items={allSchemas.map((schema) => ({
                                      value: schema.id.toString(),
                                      label: (
                                        <div className="flex items-center gap-2">
                                          <span>{schema.name}</span>
                                          <span className="text-xs text-grey-4 ml-1">
                                            {getLabelForFieldType(schema.type, !isEmpty(schema.choices))}
                                          </span>
                                        </div>
                                      ),
                                      disabled: fields.some((f) => f.id === schema.id && f.id !== field.id),
                                    }))}
                                    value={newField.id?.toString()}
                                    onValueChange={(value) => handleChangeFieldSchema(Number(value))}
                                    filterPredicate={(item, searchTerm) => {
                                      const schema = allSchemas.find((s) => s.id.toString() === item.value);
                                      return schema.name.toLowerCase().includes(searchTerm.toLowerCase());
                                    }}
                                    placeholder="Select field(s) to add"
                                  />
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button variant="link" size="xs" className="self-start px-0">
                                    <PlusIcon className="mr-2" />
                                    New Field
                                  </Button>
                                  <Button variant="ghost" size="smallIcon" className="ml-auto text-destructive" onClick={() => handleRemoveField(field.id)}>
                                    <TrashIcon fontSize={12} />
                                  </Button>
                                  <Button variant="outline" size="smallIcon" onClick={handleCancelEditField}>
                                    <XmarkIcon fontSize={12} />
                                  </Button>
                                  <Button size="smallIcon" onClick={() => handleSaveEditedField(field.id)} disabled={!newField.id}>
                                    <CheckIcon fontSize={12} />
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="px-3 py-2 bg-card rounded-xl border border-border flex items-center gap-2 h-10">
                                <div {...provided.dragHandleProps} className="flex items-center justify-center">
                                  <GripDotsVerticalIcon fontSize={12} className="text-grey-3" />
                                </div>
                                <div className="flex-1 text-xs font-medium">
                                  <span className="text-foreground">{field.label}</span>
                                  {field.required && <span className="text-red-4">*</span>}
                                </div>
                                <Button
                                  variant="ghost"
                                  size="smallIcon"
                                  onClick={() => handleEditField(field.id)}
                                  disabled={!!editingFieldId || isAddingField || field.source === 'dbColumn'}
                                >
                                  <PenIcon fontSize={12} />
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {/* Add New Field Section */}
            {isAddingField && (
              <div className="self-stretch px-3 py-2 bg-background rounded-xl border border-border flex flex-col gap-2">
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between items-center">
                    <label className="text-xs font-medium">Add Field</label>
                    <div className="flex items-center gap-2">
                      <Switch size="small" checked={newField.required} onChange={(checked) => setNewField({ ...newField, required: checked })} />
                      <span className="text-foreground text-sm">Required</span>
                    </div>
                  </div>
                  <SelectCombobox
                    items={allSchemas.map((schema) => ({
                      value: schema.id.toString(),
                      label: (
                        <div className="flex items-center gap-2">
                          <span>{schema.name}</span>
                          <span className="text-xs text-grey-4 ml-1">
                            {getLabelForFieldType(schema.type, !isEmpty(schema.choices))}
                          </span>
                        </div>
                      ),
                      disabled: fields.some((field) => field.id === schema.id),
                    }))}
                    value={newField.id?.toString()}
                    onValueChange={(value) => handleChangeFieldSchema(Number(value))}
                    filterPredicate={(item, searchTerm) => {
                      const schema = allSchemas.find((schema) => schema.id.toString() === item.value);
                      return schema.name.toLowerCase().includes(searchTerm.toLowerCase());
                    }}
                    placeholder="Select field(s) to add"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="link" size="xs" className="self-start px-0" onClick={() => setShowNewFormFieldModal(true)}>
                    <PlusIcon className="mr-2" />
                    New Field
                  </Button>
                  <Button variant="outline" size="smallIcon" className="ml-auto" onClick={handleCancelAddField}>
                    <XmarkIcon fontSize={12} />
                  </Button>
                  <Button size="smallIcon" onClick={handleAddField} disabled={!newField.id}>
                    <CheckIcon fontSize={12} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        <NewFormFieldModal onSaveField={handleSaveNewField} />
      </AccordionContent>
    </AccordionItem>
  );
};
